import axios from "axios";
import config from "./config";
import { refreshWithLatestToken, withRefreshLock } from "./utils/refreshLock";

const api = axios.create({
  baseURL: config.apiBaseUrl,
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

api.interceptors.request.use(config => {
  const token = localStorage.getItem("access_token");
  if (token) {
    config.headers["Authorization"] = "Bearer " + token;
  }
  return config;
});

api.interceptors.response.use(
  response => response,
  async error => {
    const originalRequest = error.config;

    if (
      error.response &&
      error.response.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url.includes("/token/refresh") &&
      !originalRequest.url.includes("/login") &&
      !originalRequest.url.includes("/logout")
    ) {
      if (isRefreshing) {
        return new Promise(function (resolve, reject) {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            originalRequest.headers["Authorization"] = "Bearer " + token;
            return api(originalRequest);
          })
          .catch(err => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const postRefresh = async (refreshToken) => {
        const response = await api.post(`${config.apiBaseUrl}/api/token/refresh/`, {
          refresh: refreshToken,
        });
        const newToken = response.data.access;
        localStorage.setItem("access_token", newToken);
        if (response.data.refresh) {
          localStorage.setItem("refresh_token", response.data.refresh);
        }
        return newToken;
      };

      try {
        const newToken = await withRefreshLock(async () => {
          if (!localStorage.getItem("refresh_token")) {
            const missingError = new Error("No refresh token available");
            missingError.clearSession = true;
            throw missingError;
          }
          return refreshWithLatestToken(postRefresh);
        });

        api.defaults.headers.common["Authorization"] = "Bearer " + newToken;
        processQueue(null, newToken);
        originalRequest.headers["Authorization"] = "Bearer " + newToken;
        return api(originalRequest);
      } catch (err) {
        processQueue(err, null);
        const storedRefresh = localStorage.getItem("refresh_token");
        const failedCurrentToken = !storedRefresh || storedRefresh === err.attemptedRefresh;
        if (err.clearSession || failedCurrentToken) {
          localStorage.removeItem("access_token");
          localStorage.removeItem("refresh_token");
          if (!(window.location.pathname || "").startsWith("/login")) {
            window.location.href = "/login";
          }
        }
        return Promise.reject(err.clearSession ? error : err);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default api;
